import { db, get, getAll, meta, put, setMeta, type StoreName } from "../db/idb.ts";
import { done, failed, pending, depth, reject, rejectedCount, rejected as rejectedList,
         type OutboxOp } from "../db/outbox.ts";
import { MAX_ATTEMPTS } from "../config.ts";
import { Superseded, type Transport } from "./transport.ts";

export interface SyncState {
  online: boolean;
  running: boolean;
  queued: number;
  /** How many queued writes the last run actually delivered. */
  pushed: number;
  /** Writes set aside because the server keeps refusing them. */
  rejected: number;
  /** Fields whose patch reached the server and lost to a newer value. */
  superseded: string[];
  last_sync_at: string | null;
  last_error: string | null;
}

type Listener = (s: SyncState) => void;

const msgOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * A queued insert carries the row as it looked when it was made. For rows
 * that never change that is exactly right. A session is not one of them: it
 * is created as HEATING and is usually finished — sometimes days later, on a
 * device that has been offline the whole time — before the insert is ever
 * sent. Sending the original snapshot then puts an evening on the server as
 * still burning, and the server allows only one of those per sauna, so the
 * next evening collides with a ghost.
 *
 * So the current row goes up, not the remembered one. The patches that
 * follow are then harmless: they set values that already match.
 */
async function freshen(op: OutboxOp): Promise<OutboxOp> {
  if (op.kind !== "insert_session") return op;
  const current = await get<Record<string, unknown>>("heat_session", op.row["id"] as string);
  if (!current) return op;
  return { ...op, row: { ...current, client_id: op.row["client_id"] } };
}

/** The row this write brings into existence, if any. */
function createdId(op: OutboxOp): string | null {
  switch (op.kind) {
    case "insert_session": case "insert_measurement": case "insert_group":
      return op.row["id"] as string;
    default: return null;
  }
}

/** The row this write needs to already be there. */
function dependsOn(op: OutboxOp): string | null {
  switch (op.kind) {
    case "patch_session": return op.session_id;
    case "patch_group": return op.id;
    case "update_readings": return op.measurement_id;
    case "insert_measurement": case "insert_group": case "insert_fuel":
    case "upsert_rating": case "insert_comment":
      return (op.row["session_id"] as string) ?? null;
    default: return null;
  }
}

/** Worth retrying forever. Anything else has a server's answer behind it. */
const isNetwork = (m: string) =>
  /failed to fetch|networkerror|load failed|network request failed|timeout|aborted/i.test(m);

/**
 * Push first, then pull.
 *
 * Push before pull because the device's own unsent work is the only copy
 * that exists; pulling first could overwrite a local row with a server
 * version that predates it. Push failures do not stop the pull — a phone
 * with one bad row queued should still get everyone else's evening.
 */
export class SyncEngine {
  private listeners = new Set<Listener>();
  private pushed = 0;
  private rejectedThisRun = 0;
  private supersededThisRun: string[] = [];
  private state: SyncState = {
    online: true, running: false, queued: 0, pushed: 0, rejected: 0,
    superseded: [], last_sync_at: null, last_error: null,
  };

  private transport: Transport;
  private sauna_id: string;
  constructor(transport: Transport, sauna_id: string) {
    this.transport = transport;
    this.sauna_id = sauna_id;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private emit(patch: Partial<SyncState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  async refreshQueueDepth() { this.emit({ queued: await depth() }); }

  async run(): Promise<SyncState> {
    if (this.state.running) return this.state;
    this.emit({ running: true, last_error: null });
    const errors: string[] = [];
    this.pushed = 0;
    this.rejectedThisRun = 0;
    this.supersededThisRun = [];

    /* The sauna has to exist on the server before anything that refers to it.
       This used to happen as a side effect of the pull, which ran after a
       push that was guaranteed to fail — so the error the user saw was
       "row-level security policy for table heat_session" while the actual
       blocker was that the sauna had never been sent. The precondition is
       now established first, and its own failure is recorded as its own. */
    if (!(await meta<string>("pull_cursor"))) {
      try {
        await this.ensureSaunaOnServer();
        await setMeta("claim_error", null);
      } catch (e) {
        const m = msgOf(e);
        await setMeta("claim_error", m);
        errors.push(`sauna ei mennyt palvelimelle: ${m}`);
      }
    }

    /* Push before pull: the device's own unsent work is the only copy that
       exists, and pulling first could overwrite a local row with a server
       version that predates it. A failed push does not cancel the pull. */
    try { await this.push(); } catch (e) { errors.push(msgOf(e)); }

    let claimed = false;
    try { claimed = await this.pull(); } catch (e) { errors.push(msgOf(e)); }

    // If the pull created the sauna those queued rows were waiting for,
    // there is no reason to make the user wait for the next sync.
    if (claimed && errors.length) {
      errors.length = 0;
      try { await this.push(); } catch (e) { errors.push(msgOf(e)); }
    }

    if (!errors.length) await setMeta("last_sync_at", new Date().toISOString());
    this.emit({
      running: false,
      queued: await depth(),
      pushed: this.pushed,
      rejected: await rejectedCount(),
      superseded: this.supersededThisRun,
      last_sync_at: (await meta<string>("last_sync_at")) ?? null,
      last_error: errors.length ? errors.join("; ") : null,
    });
    return this.state;
  }

  /**
   * Strictly in order, and it stops at the first failure rather than
   * skipping ahead: a patch that follows an insert is meaningless if the
   * insert has not landed. The failed entry keeps its place and its attempt
   * count, so nothing is lost and nothing is applied out of order.
   */
  private async push(): Promise<void> {
    const queue = (await pending()).sort((a, b) => a.seq - b.seq);

    /* Rows whose parent was set aside can never succeed: a patch to a session
       that was never accepted asks about something the server has no record
       of. Retrying them three times each only fills the log with a reason
       that names the child instead of the parent. */
    const orphaned = new Set<string>();
    for (const r of await rejectedList()) {
      const made = createdId(r.op);
      if (made) orphaned.add(made);
    }

    for (const entry of queue) {
      const needs = dependsOn(entry.op);
      if (needs && orphaned.has(needs)) {
        await reject(entry.seq, "sen ilta jäi lähettämättä, joten tätä ei voi liittää mihinkään");
        this.rejectedThisRun += 1;
        const made = createdId(entry.op);
        if (made) orphaned.add(made);
        continue;
      }
      try {
        await this.transport.apply(await freshen(entry.op));
        await done(entry.seq);
        this.pushed += 1;
      } catch (e) {
        /* Delivered, answered, and beaten by a newer value. Retrying cannot
           change that, and holding the queue behind it would stop everything
           recorded afterwards — so it leaves the queue like a success and is
           reported like the loss it is. */
        if (e instanceof Superseded) {
          await done(entry.seq);
          this.supersededThisRun.push(e.field);
          continue;
        }
        const msg = e instanceof Error ? e.message : String(e);
        await failed(entry.seq, msg);

        /* A lost network is worth waiting for; a row the server will never
           accept is not. Without this, one refused write sits at the head of
           the queue and everything recorded afterwards waits behind it for
           good — the failure mode that hides an evening's work behind a
           single mistyped time. Refusals still get a few attempts first,
           because some of them stop being refusals: a row can be rejected
           only because something it depends on has not landed yet. */
        if (!isNetwork(msg) && entry.attempts + 1 >= MAX_ATTEMPTS) {
          await reject(entry.seq, msg);
          this.rejectedThisRun += 1;
          const made = createdId(entry.op);
          if (made) orphaned.add(made);
          continue;
        }
        throw new Error(`sync stopped at #${entry.seq}: ${msg}`);
      }
    }
  }

  /**
   * The server's version replaces the local one for rows that are not
   * waiting in the outbox. A row that IS queued is left alone: the device
   * holds the newer truth until it has been sent.
   */
  private async pull(): Promise<boolean> {
    let claimed = false;
    const since = null;   // the whole sauna, every time — see the transport
    const result = await this.transport.pull(this.sauna_id, since);
    const held = await heldIds();

    for (const [table, rows] of Object.entries(result.rows)) {
      if (!rows) continue;
      for (const row of rows) {
        const id = row["id"] as string;
        if (held.has(id)) continue;
        await put(table as StoreName, row as { id: string });
      }
    }
    /* The first successful pull is also when the device finds out whether it
       is joining a sauna that already exists or bringing its own. A sauna
       made offline is only pushed once we know the server has none — pushing
       it eagerly would give someone who was invited to a sauna a second,
       empty one of their own. */
    const server = result.rows["sauna"]?.[0];
    if (server?.["id"]) {
      await setMeta("sauna_id", server["id"] as string);
      this.sauna_id = server["id"] as string;
    } else if (!(await meta<boolean>("sauna_claimed"))) {
      claimed = await this.claimLocalSauna();
    }
    await setMeta("pull_cursor", result.server_time);
    return claimed;
  }

  /**
   * Make sure this device's sauna exists on the server before anything is
   * pushed against it. Asking first matters: a device invited into someone
   * else's sauna must not bring an empty one of its own.
   */
  private async ensureSaunaOnServer(): Promise<void> {
    const mine = await this.transport.mySaunas();
    /* A person can belong to several saunas (FR-4.1) and this device works
       with one of them: the one it was told about, which after redeeming an
       invitation is the sauna it was invited to. Taking whichever row the
       server happened to return first would move the device to the other one
       on its first sync. */
    const want = await meta<string>("sauna_id");
    const server = mine.find((s) => s["id"] === want) ?? mine[0];
    if (server?.["id"]) {
      // Someone — probably this person on another device — already has one.
      // Join it; do not bring an empty second sauna along.
      await put("sauna", server as { id: string });
      await setMeta("sauna_id", server["id"] as string);
      await setMeta("sauna_claimed", true);
      this.sauna_id = server["id"] as string;
      return;
    }
    if (await meta<boolean>("sauna_claimed")) return;
    await this.claimLocalSauna();
  }

  /**
   * Send up the sauna, stove and points this device made on its own. Applied
   * straight through rather than queued: everything already in the queue was
   * written against this sauna, so it cannot go behind them in the order.
   */
  private async claimLocalSauna(): Promise<boolean> {
    const sauna = await get<Record<string, unknown>>("sauna", this.sauna_id);
    if (!sauna) return false;
    const stove = (await getAll<Record<string, unknown>>("stove"))
      .find((s) => s["sauna_id"] === this.sauna_id);
    if (!stove) return false;
    const positions = (await getAll<Record<string, unknown>>("position"))
      .filter((p) => p["stove_id"] === stove["id"]);
    await this.transport.apply({ kind: "insert_sauna", row: sauna, stove, positions });
    await setMeta("sauna_claimed", true);
    return true;
  }
}

/** Ids with unsent local changes; the pull must not clobber these. */
async function heldIds(): Promise<Set<string>> {
  const out = new Set<string>();
  for (const e of await pending()) {
    const op = e.op;
    switch (op.kind) {
      case "insert_sauna": case "upsert_position":
        out.add(op.row["id"] as string); break;
      case "insert_session": case "insert_measurement": case "insert_group":
      case "insert_fuel": case "upsert_rating": case "insert_comment":
      case "insert_stove_event":
        out.add(op.row["id"] as string); break;
      case "patch_session": out.add(op.session_id); break;
      case "patch_group": case "update_sauna": out.add(op.id); break;
      case "update_readings": out.add(op.measurement_id); break;
    }
  }
  return out;
}

/** Sync when the app opens, when it comes back to the foreground, and when
 *  the network returns. The user is never asked to press anything (FR-11.3). */
export function autoSync(engine: SyncEngine, everyMs = 60_000): () => void {
  const go = () => { void engine.run(); };
  const onOnline = () => { engine["emit"]?.({ online: true }); go(); };
  const onOffline = () => { engine["emit"]?.({ online: false }); };
  const onVisible = () => { if (document.visibilityState === "visible") go(); };
  /* A tab left open on the kitchen table should notice what the phone at the
     sauna recorded. Nothing pushes to it, so it asks — rarely, and only while
     someone is actually looking at it. */
  const timer = setInterval(() => {
    if (document.visibilityState === "visible" && navigator.onLine) go();
  }, everyMs);

  addEventListener("online", onOnline);
  addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);
  go();
  return () => {
    clearInterval(timer);
    removeEventListener("online", onOnline);
    removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export async function queueDepth(): Promise<number> {
  return (await db()).count("outbox");
}
